import { Layout, Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext, useState } from 'react';
import AccountCommunication from '../utils/AccountCommunication';

const { WrappedDropdownWithLabel, WrappedInputNumberWithLabel } = UI;
const { UserCaps, Util } = Utils;
const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { LayoutContext } = Layout;

const PermissionsModal = ({
	data: {
		callback,
		parentPerPrintLimit,
		selectedLocations,
		MAXIMUM_LIMIT_AMOUNT,
	},
	modalId,
}) => {
	const { closeModal, showError } = useContext(ModalContext);
	const { isMobile } = useContext(LayoutContext);
	const enabledDisabledOptions = [
		{
			name: 'Enabled',
			value: true,
		},
		{
			name: 'Disabled',
			value: false,
		},
	];
	const optionalRequiredOptions = [
		{
			name: 'Optional',
			value: true,
		},
		{
			name: 'Required',
			value: false,
		},
	];
	const perPrintLimitOptions = [
		{
			name: 'No Limit',
			value: false,
		},
		{
			name: 'Limit To',
			value: true,
		},
	];

	/**
	 * Return true if the passed in property matches for all locations
	 */
	const locationsPropertyMatch = property => {
		let propertyValue;

		selectedLocations.forEach((loc, index) => {
			if (index === 0) {
				propertyValue = loc.MeterAccount[property];
			} else if (loc.MeterAccount[property] !== propertyValue) {
				return false;
			}
		});

		return true;
	};

	/**
	 * Set initial states
	 */
	// locations
	const [locations, setLocations] = useState([...selectedLocations]);
	// add funds
	const [ucPurchasePostage, setUcPurchasePostage] = useState(null);
	// per print limit dropdown
	const [perPrintLimitDropdown, setPerPrintLimitDropdown] = useState(null);
	// per print limit value
	const [perPrintLimit, setPerPrintLimit] = useState(
		locationsPropertyMatch('PerPrintLimit')
			? selectedLocations[0].MeterAccount.PerPrintLimit
			: 0
	);
	// view reports
	const [ucWebBasedReports, setUcWebBasedReports] = useState(null);
	// store purchases
	const [ucPurchaseFromStore, setUcPurchaseFromStore] = useState(null);
	// cost codes
	const [ucReferenceCodeOptional, setUcReferenceCodeOptional] = useState(null);
	// disables Update button
	const [isUpdating, setIsUpdating] = useState(false);

	const updateLocationsProperty = (propertyName, value) => {
		const updatedLocations = [...locations];

		updatedLocations.forEach((loc, index) => {
			updatedLocations[index].MeterAccount[propertyName] = value;
		});

		setLocations(updatedLocations);
	};

	/**
	 * Make API call to update the locations
	 */
	const updateLocations = async () => {
		setIsUpdating(true);

		const response = await AccountCommunication.updateLocations(locations);

		setIsUpdating(false);

		if (!AccountCommunication.isSuccessful(response)) {
			showError({
				response,
			});
		} else {
			closeModal(modalId);
		}

		callback(response);
	};

	/**
	 * Iterate through the selected locations and update the specified user cap for each location
	 */
	const updateCapabilities = (capName, value) => {
		const updatedLocations = [...locations];

		updatedLocations.forEach((loc, index) => {
			const prevCaps = loc.MeterAccount.Capabilities;
			const newCaps = value
				? Util.setBit(prevCaps, UserCaps[capName])
				: Util.removeBit(prevCaps, UserCaps[capName]);

			updatedLocations[index].MeterAccount.Capabilities = newCaps;
		});

		setLocations(updatedLocations);
	};

	/**
	 * Update locations with changed values and make API update call
	 */
	const onUpdateLocationsButtonClick = () => {
		// if add funds changed, update user caps for all selected locations
		if (ucPurchasePostage !== null) {
			updateCapabilities('ucPurchasePostage', ucPurchasePostage);
		}

		// if add funds changed, update user caps for all selected locations
		if (perPrintLimitDropdown !== null) {
			if (perPrintLimitDropdown) {
				// if "limit to" was selected, update locations with the perPrintLimit value
				updateLocationsProperty('PerPrintLimit', perPrintLimit);
			} else {
				// if "no limit" was selected, set to parent account limit
				updateLocationsProperty('PerPrintLimit', parentPerPrintLimit);
			}
		}

		// if view reports changed, update user caps for all selected locations
		if (ucWebBasedReports !== null) {
			updateCapabilities('ucWebBasedReports', ucWebBasedReports);
		}

		// if store purchases changed, update user caps for all selected locations
		if (ucPurchaseFromStore !== null) {
			updateCapabilities('ucPurchaseFromStore', ucPurchaseFromStore);
		}

		// if cost codes changed, update user caps for all selected locations
		if (ucReferenceCodeOptional !== null) {
			updateCapabilities('ucReferenceCodeOptional', ucReferenceCodeOptional);
		}

		// update locations
		updateLocations();
	};

	return (
		<Modal
			modalId={modalId}
			className="account-locations permissions-modal"
			header={`Bulk Update ${locations.length} Locations`}
			primaryButton={{
				type: ModalButtonActionType.Ok,
				onClick: onUpdateLocationsButtonClick,
				text: 'Update Locations',
				disabled: isUpdating,
			}}
			secondaryButton={{
				type: ModalButtonActionType.Cancel,
				text: 'Close',
			}}
			size="small"
		>
			<div className="body">
				<div className="p-grid">
					<WrappedDropdownWithLabel
						className="dropdown p-col-12"
						label="Add Funds"
						uiElementConfig={{
							name: 'ucPurchasePostage',
							value: ucPurchasePostage,
							options: enabledDisabledOptions,
							optionLabel: 'name',
							optionValue: 'value',
							placeholder: 'Select Permission',
							dataqa: 'locationbulkeditaddfunds',
							onChange: e => {
								setUcPurchasePostage(e.target.value);
							},
						}}
					/>
					<WrappedDropdownWithLabel
						className="dropdown p-col-12 p-md-7 p-lg-7"
						label="Per Print Limit"
						uiElementConfig={{
							name: 'perPrintLimitDropdown',
							value: perPrintLimitDropdown,
							options: perPrintLimitOptions,
							optionLabel: 'name',
							optionValue: 'value',
							placeholder: 'Select Limit',
							dataqa: 'locationbulkeditperprintlimitdropdown',
							onChange: e => {
								setPerPrintLimitDropdown(e.target.value);
							},
						}}
					/>
					<WrappedInputNumberWithLabel
						className={`dollar-amount-field p-col-12 p-md-5 p-lg-5 ${
							isMobile ? 'is-mobile' : ''
						}`}
						label=""
						uiElementConfig={{
							name: 'perPrintLimit',
							value: !perPrintLimitDropdown ? 0 : perPrintLimit,
							step: 1,
							min: 0,
							max: MAXIMUM_LIMIT_AMOUNT,
							minFractionDigits: 2,
							maxFracionDigits: 2,
							mode: 'currency',
							currency: 'USD',
							locale: 'en-US',
							dataqa: 'restricttoamount',
							disabled: !perPrintLimitDropdown,
							showButtons: true,
							onChange: e => {
								setPerPrintLimit(e.target.value);
							},
						}}
					/>
					<WrappedDropdownWithLabel
						className="dropdown p-col-12"
						label="View Reports"
						uiElementConfig={{
							name: 'ucWebBasedReports',
							value: ucWebBasedReports,
							options: enabledDisabledOptions,
							optionLabel: 'name',
							optionValue: 'value',
							placeholder: 'Select Permission',
							dataqa: 'locationbulkeditviewreports',
							onChange: e => {
								setUcWebBasedReports(e.target.value);
							},
						}}
					/>
					<WrappedDropdownWithLabel
						className="dropdown p-col-12"
						label="Store Purchases"
						uiElementConfig={{
							name: 'ucPurchaseFromStore',
							value: ucPurchaseFromStore,
							options: enabledDisabledOptions,
							optionLabel: 'name',
							optionValue: 'value',
							placeholder: 'Select Permission',
							dataqa: 'locationbulkeditstorepurchased',
							onChange: e => {
								setUcPurchaseFromStore(e.target.value);
							},
						}}
					/>
					<WrappedDropdownWithLabel
						className="dropdown p-col-12"
						label="Cost Codes"
						uiElementConfig={{
							name: 'ucReferenceCodeOptional',
							value: ucReferenceCodeOptional,
							options: optionalRequiredOptions,
							optionLabel: 'name',
							optionValue: 'value',
							placeholder: 'Select Permission',
							dataqa: 'locationbulkeditcostcodes',
							onChange: e => {
								setUcReferenceCodeOptional(e.target.value);
							},
						}}
					/>
				</div>
			</div>
		</Modal>
	);
};

export default PermissionsModal;
